import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { MaskLines, Reveal } from "@/components/motion";

export const SectionHead = ({ eyebrow, lines, intro, align = "left", testId }) => (
  <div className={`max-w-3xl ${align === "center" ? "mx-auto text-center" : ""}`} data-testid={testId}>
    <Reveal>
      <p className="eyebrow mb-6">{eyebrow}</p>
    </Reveal>
    <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-slate-50">
      <MaskLines lines={lines} />
    </h2>
    {intro && (
      <Reveal delay={0.2}>
        <p className="mt-6 text-sm sm:text-lg text-slate-300 leading-relaxed max-w-2xl">{intro}</p>
      </Reveal>
    )}
  </div>
);

export const CtaBand = () => (
  <section data-testid="cta-band" className="relative bg-[#0B1120] overflow-hidden">
    <div className="absolute right-[-10%] top-[-30%] w-[600px] h-[600px] rounded-full bg-[#6B2D8F]/25 blur-[150px] pointer-events-none" />
    <div className="relative max-w-[1440px] mx-auto px-5 sm:px-8 lg:px-12 py-24 sm:py-32 grid lg:grid-cols-12 gap-10 items-end">
      <div className="lg:col-span-8">
        <p className="eyebrow mb-6">Begin privately</p>
        <h2 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight text-slate-50">
          <MaskLines lines={["Your recovery starts", "with one confidential", "conversation."]} lineClassName="" />
        </h2>
      </div>
      <Reveal delay={0.3} className="lg:col-span-4 flex flex-col gap-4">
        <Link to="/contact" data-testid="cta-band-book-button" className="btn-bronze group inline-flex items-center justify-between gap-3 px-7 py-5 text-sm font-medium tracking-wide">
          Book a Confidential Consultation
          <ArrowUpRight className="w-4 h-4 transition-transform duration-500 group-hover:rotate-45" />
        </Link>
        <Link to="/immediate-support" data-testid="cta-band-urgent-button" className="inline-flex items-center justify-between gap-3 px-7 py-5 text-sm tracking-wide text-slate-100 border border-red-400/40 hover:bg-red-500/10 transition-colors duration-500">
          <span className="flex items-center gap-3"><span className="pulse-dot w-1.5 h-1.5 rounded-full bg-red-500" /> Immediate Post-Incident Support</span>
          <ArrowUpRight className="w-4 h-4" />
        </Link>
      </Reveal>
    </div>
  </section>
);

export const PageHero = ({ eyebrow, lines, intro, image, testId, urgent = false }) => (
  <section data-testid={testId} className="relative pt-40 pb-20 sm:pt-48 sm:pb-28 overflow-hidden bg-[#070A12]">
    {image && (
      <div className="absolute inset-0">
        <img src={image} alt="" className="w-full h-full object-cover opacity-35 scale-105" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#070A12] via-[#070A12]/85 to-[#070A12]/40" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#070A12] to-transparent" />
      </div>
    )}
    <div className={`absolute -left-32 top-10 w-[480px] h-[480px] rounded-full blur-[140px] pointer-events-none ${urgent ? "bg-red-600/20" : "bg-[#6B2D8F]/25"}`} />
    <div className="relative max-w-[1440px] mx-auto px-5 sm:px-8 lg:px-12">
      <p className="eyebrow mb-6 flex items-center gap-3">
        {urgent ? <span className="pulse-dot w-2 h-2 rounded-full bg-red-500" /> : <span className="w-10 h-px bg-[#C5A059]" />}
        {eyebrow}
      </p>
      <h1 className="font-display font-medium text-[2.6rem] leading-[1.02] sm:text-6xl lg:text-[5rem] tracking-tight text-slate-50 max-w-5xl">
        <MaskLines lines={lines} onLoad />
      </h1>
      {intro && (
        <Reveal delay={0.4}>
          <p className="mt-8 max-w-2xl text-sm sm:text-lg text-slate-300 leading-relaxed">{intro}</p>
        </Reveal>
      )}
    </div>
  </section>
);
